/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { View, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQueryClient } from '@tanstack/react-query';
import {
  differenceInDays,
  differenceInCalendarMonths,
  addDays,
  startOfDay,
} from 'date-fns';
import { pd } from '@airasia-phoenix/react-native/utils/utils';
import { useTranslation } from 'react-i18next';
import { useLogger } from '@airasia-rn/core-logger';
import {
  useAppConfig,
  useDeviceInfo,
  useSuperAppContext,
} from '@airasia-rn/ui';
import { useCurrency } from '@airasia-rn/ui/config';
import { StationTypeFilter, useStationsMap } from '@airasia-rn/purchases';
import { SearchStationsView } from '../SearchStationsView';
import { useSnapRecentSearches } from '../../hooks/useSnapRecentSearches';
import { useSnapSearchFormState } from '../../hooks/useSnapSearchFormState';
import { useSnapSearchViewFeatureFlag } from '../../hooks/useSnapSearchViewFeatureFlag';
import { useSnapNavigation } from '../../context/SnapNavigationContext';

import {
  StationSelectionPayload,
  SearchFormState,
  RoomConfig,
  TripType,
} from '../../types';
import { snapKeys } from '../../hooks/snap/keys';
import { useSnapLfc } from '../../hooks/snap/search';
import {
  mapLfcChunkToCalendarPrices,
  mergeCalendarPriceChunks,
} from '../../utils/lfcUtils';
import {
  buildSnapDeepLinkInitialState,
  hasSnapDeepLinkParams,
  parseSnapDeepLinkParams,
} from '../../utils/deeplink';

import {
  CalendarView,
  ChinaDisclaimerModal,
  CHINA_DISCLAIMER_IMAGE_URL,
  GuestFieldView,
} from '@airasia-rn/hotels';
import CheckBoxView from '@airasia-phoenix/react-native/components/CheckBoxView';
import ButtonView, {
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import { DialogRefs } from '@airasia-phoenix/react-native/components/DialogView/types';
import { useOptionalSnapIntegration } from '../../configs/SnapIntegrationsProvider';
import {
  MAX_LOS,
  MIN_DATE_OFFSET_DAYS,
  MIN_RETURN_DATE_OFFSET_DAYS,
  MAX_DATE_OFFSET_DAYS,
  DEFAULT_DEPARTURE_OFFSET_DAYS,
  DEFAULT_RETURN_OFFSET_DAYS,
  DEFAULT_STAY_DAYS,
  DEFAULT_ONE_WAY_STAY_DAYS,
  SNAP_GUEST_FIELD_CONFIG,
  SNAP_CHINA_DOMAIN,
  DEFAULT_CURRENCY,
  DEFAULT_GEO_ID,
  DEFAULT_LANGUAGE,
  SNAP_CALENDAR_PRICE_PLACEHOLDER,
} from '../../configs/constants';
import { useOptionalSnapConfig } from '../../configs/SnapConfigProvider';
import { buildSnapListingUrl } from '../../utils/listingUrl';
import { SnapSearchWidgetEvent } from '../../analytics';
import {
  EVENT_NAMES,
  SELECT_DIFFERENT_DATES,
  ACTIONS,
  SNAP_SEARCH_WIDGET,
} from '../../analytics/snapSearchWidget/snapSearchWidget.meta';
import { buildSnapChinaDisclaimerViewedAnalytics } from '../../analytics/snapSearchWidget/snapSearchWidget.analytics';

const calendarConfig = {
  maxLos: MAX_LOS,
  minDateOffsetDays: MIN_DATE_OFFSET_DAYS,
  minReturnDateOffsetDays: MIN_RETURN_DATE_OFFSET_DAYS,
  maxDateOffsetDays: MAX_DATE_OFFSET_DAYS,
  defaultDepartureOffsetDays: DEFAULT_DEPARTURE_OFFSET_DAYS,
  defaultReturnOffsetDays: DEFAULT_RETURN_OFFSET_DAYS,
  defaultStayDays: DEFAULT_STAY_DAYS,
  defaultOneWayStayDays: DEFAULT_ONE_WAY_STAY_DAYS,
};

type SearchFormViewProps = {
  onError: (message: string) => void;
  onFormStateChange?: (state: SearchFormState) => void;
  onSubmit: (state: SearchFormState) => void;
  recentSearchMaxCount?: number;
  transformListingUrl?: (url: string) => string;
};

const SearchFormViewContent = ({
  onError,
  onFormStateChange,
  onSubmit,
  recentSearchMaxCount, // eslint-disable-line @typescript-eslint/no-unused-vars
  transformListingUrl,
  initialFormState,
  syncInitialFormState,
}: SearchFormViewProps & {
  initialFormState: SearchFormState;
  syncInitialFormState: boolean;
}) => {
  const {
    snap_lfc_enabled,
    snap_combined_date_field_enabled,
    hotel_snap_unified_listing_enabled,
    snap_one_way_enabled,
  } = useSnapSearchViewFeatureFlag();
  const TripTypeSelector = useOptionalSnapIntegration('TripTypeSelector');
  const { t } = useTranslation('snap');
  const { t: tHotels } = useTranslation('hotels');
  const { logEvent } = useLogger();
  const { config: appConfig } = useAppConfig();
  const snapConfig = useOptionalSnapConfig()?.config;
  const superAppContext = useSuperAppContext();
  const { currency } = useCurrency();
  const queryClient = useQueryClient();
  const [visibleMonths, setVisibleMonths] = useState<Date[][]>([]);
  const visibleMonthsRef = useRef<Date[][]>([]);
  const chinaDisclaimerRef = useRef<DialogRefs>(null);

  const handleViewableItems = useCallback(
    (incoming: Date[][]) => {
      const current = visibleMonthsRef.current;
      if (
        incoming.length === current.length &&
        incoming.every(
          (range, i) =>
            range[0]?.getTime() === current[i]?.[0]?.getTime() &&
            range[1]?.getTime() === current[i]?.[1]?.getTime(),
        )
      ) {
        void queryClient.invalidateQueries({
          queryKey: snapKeys.search.lfc.all,
        });
        return;
      }
      visibleMonthsRef.current = incoming;
      setVisibleMonths(incoming);
    },
    [queryClient],
  );

  const [stationErrors, setStationErrors] = useState<{
    origin?: string;
    destination?: string;
  }>({});

  // Main form state using TanStack Query for persistence
  const { formState, updateFormState: updateFormStateQuery } =
    useSnapSearchFormState(initialFormState, {
      syncInitialValues: syncInitialFormState,
    });

  const snapListingRedirectUrl = useMemo(() => {
    const listingUrl = buildSnapListingUrl({
      state: formState,
      env: appConfig.env,
      countryCode:
        snapConfig?.geoId ??
        superAppContext.geoId ??
        appConfig.geoId ??
        DEFAULT_GEO_ID,
      currency: currency ?? appConfig.currency ?? DEFAULT_CURRENCY,
      language: appConfig.locale ?? DEFAULT_LANGUAGE,
      hotel_snap_unified_listing_enabled,
    });

    return transformListingUrl?.(listingUrl) ?? listingUrl;
  }, [
    appConfig.currency,
    appConfig.env,
    appConfig.geoId,
    appConfig.locale,
    currency,
    formState,
    hotel_snap_unified_listing_enabled,
    snapConfig?.geoId,
    superAppContext.geoId,
    transformListingUrl,
  ]);

  const handleFlightCalendarOpenIntent = useCallback(() => {
    const originCode = formState.originStation?.StationCode;
    const destinationCode = formState.destinationStation?.StationCode;

    if (
      !originCode ||
      !destinationCode ||
      visibleMonthsRef.current.length === 0
    ) {
      return false;
    }

    void queryClient.invalidateQueries({
      queryKey: snapKeys.search.lfc.route(
        originCode,
        destinationCode,
        currency ?? 'MYR',
      ),
    });

    if (formState.tripType === TripType.ROUND_TRIP) {
      void queryClient.invalidateQueries({
        queryKey: snapKeys.search.lfc.route(
          destinationCode,
          originCode,
          currency ?? 'MYR',
        ),
      });
    }

    return false;
  }, [
    currency,
    formState.destinationStation?.StationCode,
    formState.originStation?.StationCode,
    formState.tripType,
    queryClient,
  ]);

  const { departChunks, returnChunks } = useSnapLfc({
    originCode: formState.originStation?.StationCode ?? '',
    destinationCode: formState.destinationStation?.StationCode ?? '',
    currency: currency ?? 'MYR',
    tripType:
      formState.tripType === TripType.ONE_WAY ? 'one-way' : 'round-trip',
    visibleMonths,
  });

  const calendarMinDate = useMemo(() => new Date(), []);
  const calendarMaxDate = useMemo(
    () => addDays(calendarMinDate, MAX_DATE_OFFSET_DAYS),
    [calendarMinDate],
  );

  const totalCalendarMonths = useMemo(
    () => differenceInCalendarMonths(calendarMaxDate, calendarMinDate) + 1,
    [calendarMaxDate, calendarMinDate],
  );

  const departPriceValues = useMemo(() => {
    const chunkPrices = departChunks
      .filter((chunk) => chunk.isFetched && !chunk.isError && !chunk.isLoading)
      .map((chunk) =>
        mapLfcChunkToCalendarPrices(
          chunk.data,
          chunk.chunk,
          calendarMinDate,
          calendarMaxDate,
        ),
      );
    const merged = mergeCalendarPriceChunks(chunkPrices);
    if (merged.length === 0) return undefined;
    // Pad to full calendar length so no index is `undefined`
    for (let i = 0; i < totalCalendarMonths; i++) {
      if (!merged[i]) merged[i] = {};
    }
    return merged;
  }, [calendarMaxDate, calendarMinDate, departChunks, totalCalendarMonths]);

  const returnPriceValues = useMemo(() => {
    if (formState.tripType !== TripType.ROUND_TRIP) return undefined;
    const chunkPrices = returnChunks
      .filter((chunk) => chunk.isFetched && !chunk.isError && !chunk.isLoading)
      .map((chunk) =>
        mapLfcChunkToCalendarPrices(
          chunk.data,
          chunk.chunk,
          calendarMinDate,
          calendarMaxDate,
        ),
      );
    const merged = mergeCalendarPriceChunks(chunkPrices);
    if (merged.length === 0) return undefined;
    for (let i = 0; i < totalCalendarMonths; i++) {
      if (!merged[i]) merged[i] = {};
    }
    return merged;
  }, [
    calendarMaxDate,
    calendarMinDate,
    returnChunks,
    formState.tripType,
    totalCalendarMonths,
  ]);

  // Update form state helper with callback
  const updateFormState = useCallback(
    (updates: Partial<SearchFormState>) => {
      updateFormStateQuery(updates);
      // Notify parent component of state change
      const newState = { ...formState, ...updates };
      onFormStateChange?.(newState);
    },
    [updateFormStateQuery, formState, onFormStateChange],
  );

  // Handle rooms update
  const handleRoomsUpdate = useCallback(
    (newRooms: RoomConfig[]) => {
      updateFormState({ rooms: newRooms });
    },
    [updateFormState],
  );

  // Handle trip type change
  const handleTripTypeChange = useCallback(
    (newTripType: TripType) => {
      // Track trip type tab tap event
      const tripTypeLabel =
        newTripType === TripType.ONE_WAY ? 'oneway' : 'roundtrip';
      const tripTypeParams = SnapSearchWidgetEvent.tripTypeTabEvent({
        tripType: tripTypeLabel as 'roundtrip' | 'oneway',
      });
      logEvent(EVENT_NAMES.TAP, tripTypeParams);
      // Reset dates when switching trip types
      if (newTripType === TripType.ONE_WAY) {
        const updates: Partial<SearchFormState> = {
          selectedDates: {
            ...formState.selectedDates,
            return: formState.selectedDates.depart
              ? addDays(
                  formState.selectedDates.depart,
                  MIN_RETURN_DATE_OFFSET_DAYS,
                )
              : undefined,
          },
        };

        // Sync hotel dates if different dates is selected
        if (formState.selectDifferentDates) {
          updates.hotelDates = {
            checkIn: formState.selectedDates.depart,
            checkOut: formState.selectedDates.depart
              ? addDays(
                  formState.selectedDates.depart,
                  DEFAULT_ONE_WAY_STAY_DAYS,
                )
              : undefined,
          };
        }

        updateFormState({ tripType: newTripType, ...updates });
      } else {
        // Switch back to round-trip, set return date relative to current depart
        const departDate =
          formState.selectedDates.depart ||
          addDays(new Date(), DEFAULT_DEPARTURE_OFFSET_DAYS);
        const returnDate = addDays(departDate, DEFAULT_STAY_DAYS);

        const updates: Partial<SearchFormState> = {
          selectedDates: {
            depart: departDate,
            return: returnDate,
          },
        };

        // Sync hotel dates if different dates is selected
        if (formState.selectDifferentDates) {
          updates.hotelDates = {
            checkIn: departDate,
            checkOut: returnDate,
          };
        }

        updateFormState({ tripType: newTripType, ...updates });
      }
    },
    [formState, updateFormState, logEvent],
  );

  // Handle date confirmation from calendar
  const handleDateConfirm = (checkIn: Date, checkOut: Date | undefined) => {
    const updates: Partial<SearchFormState> = {
      selectedDates: { depart: checkIn, return: checkOut },
    };

    // Sync hotel dates if different dates is selected
    if (formState.selectDifferentDates) {
      updates.hotelDates = {
        checkIn,
        checkOut:
          checkOut ||
          (checkIn ? addDays(checkIn, DEFAULT_ONE_WAY_STAY_DAYS) : undefined),
      };
    }

    updateFormState(updates);
  };

  // Handle date reset
  const handleResetDates = () => {
    const today = startOfDay(new Date());
    const defaultDepart = addDays(today, DEFAULT_DEPARTURE_OFFSET_DAYS);
    const defaultReturn =
      formState.tripType === TripType.ONE_WAY
        ? undefined
        : addDays(today, DEFAULT_RETURN_OFFSET_DAYS);

    const updates: Partial<SearchFormState> = {
      selectedDates: {
        depart: defaultDepart,
        return: defaultReturn,
      },
    };

    // Sync hotel dates if different dates is selected
    if (formState.selectDifferentDates) {
      updates.hotelDates = {
        checkIn: defaultDepart,
        checkOut:
          defaultReturn || addDays(defaultDepart, DEFAULT_ONE_WAY_STAY_DAYS),
      };
    }

    updateFormState(updates);
  };

  // Handle select different dates checkbox toggle
  const handleSelectDifferentDatesToggle = () => {
    const newValue = !formState.selectDifferentDates;

    // Create event data following hotels package pattern
    const eventData = Platform.select({
      default: {
        action: ACTIONS.TAP,
        category: SELECT_DIFFERENT_DATES.CATEGORY,
        label: newValue
          ? SELECT_DIFFERENT_DATES.DIFFERENT_DATES_TICK
          : SELECT_DIFFERENT_DATES.DIFFERENT_DATES_UNTICK,
        screenName: SNAP_SEARCH_WIDGET.SCREEN_NAME,
        lineofbusiness: SNAP_SEARCH_WIDGET.LINE_OF_BUSINESS,
      },
    });

    // Log the checkbox toggle event
    if (eventData) {
      logEvent(ACTIONS.TAP, eventData);
    }

    // When enabling, sync hotel dates with flight dates
    if (newValue) {
      updateFormState({
        selectDifferentDates: newValue,
        hotelDates: {
          checkIn: formState.selectedDates.depart,
          checkOut:
            formState.selectedDates.return ||
            (formState.selectedDates.depart
              ? addDays(
                  formState.selectedDates.depart,
                  DEFAULT_ONE_WAY_STAY_DAYS,
                )
              : undefined),
        },
      });
    } else {
      updateFormState({
        selectDifferentDates: newValue,
      });
    }
  };

  // Handle hotel date confirmation from calendar
  const handleHotelDateConfirm = (
    checkIn: Date,
    checkOut: Date | undefined,
  ) => {
    updateFormState({
      hotelDates: { checkIn, checkOut },
    });
  };

  // Handle hotel date reset
  const handleHotelResetDates = () => {
    updateFormState({
      hotelDates: {
        checkIn: formState.selectedDates.depart,
        checkOut: formState.selectedDates.return,
      },
    });
  };
  // Create hotel calendar config with constrained dates
  const hotelCalendarConfig = useMemo(() => {
    // For hotel calendar, constrain to flight date range using direct dates
    // This ensures only dates within flight depart and return are enabled
    const hotelMinDate = formState.selectedDates.depart
      ? startOfDay(formState.selectedDates.depart)
      : undefined;
    const hotelMaxDate = formState.selectedDates.return
      ? formState.tripType === TripType.ROUND_TRIP
        ? startOfDay(formState.selectedDates.return)
        : undefined
      : undefined;
    const hotelMaxLos =
      formState.selectedDates.depart && formState.selectedDates.return
        ? Math.max(
            1,
            differenceInDays(
              startOfDay(formState.selectedDates.return),
              startOfDay(formState.selectedDates.depart),
            ),
          )
        : MAX_LOS;
    return {
      ...calendarConfig,
      minDate: hotelMinDate,
      maxDate: hotelMaxDate,
      maxLos: hotelMaxLos,
    };
  }, [
    formState.selectedDates.depart,
    formState.selectedDates.return,
    formState.tripType,
  ]);

  // Handle station selection from SearchStationsView
  const handleStationSelect = useCallback(
    (params: StationSelectionPayload) => {
      const nextOrigin = params.from?.StationCode ?? null;
      const nextDestination = params.to?.StationCode ?? null;
      const currentOrigin = formState.originStation?.StationCode ?? null;
      const currentDestination =
        formState.destinationStation?.StationCode ?? null;
      const stationsChanged =
        nextOrigin !== currentOrigin || nextDestination !== currentDestination;

      updateFormState({
        originStation: params.from,
        destinationStation: params.to,
        fromIsAllAirports:
          params.from !== null ? (params.fromIsAllAirports ?? false) : false,
        toIsAllAirports:
          params.to !== null ? (params.toIsAllAirports ?? false) : false,
      });
      // Clear errors when stations are selected
      setStationErrors({});
      if (stationsChanged) {
        visibleMonthsRef.current = [];
        setVisibleMonths([]);
      }
    },
    [formState.destinationStation, formState.originStation, updateFormState],
  );

  const handleSearch = () => {
    const isChinaWebRoute =
      Platform.OS === 'web' &&
      typeof window !== 'undefined' &&
      (window.location.hostname.endsWith(SNAP_CHINA_DOMAIN) ||
        window.location.pathname.toLowerCase().includes('/zh-cn'));

    if (isChinaWebRoute) {
      chinaDisclaimerRef.current?.openModal();
      return;
    }

    const newErrors: { origin?: string; destination?: string } = {};

    if (!formState.originStation) {
      newErrors.origin = t('searchSnapPage.stationField.fieldRequired');
    }
    if (!formState.destinationStation) {
      newErrors.destination = t('searchSnapPage.stationField.fieldRequired');
    }

    if (Object.keys(newErrors).length > 0) {
      setStationErrors(newErrors);
      return;
    }

    // Track search button click event (aa_snp_search)
    if (formState.originStation && formState.destinationStation) {
      const adults = formState.rooms.reduce(
        (acc, room) => acc + room.adults,
        0,
      );
      const kids = formState.rooms.reduce(
        (acc, room) => acc + (room.children?.length ?? 0),
        0,
      );
      const infants = formState.rooms.reduce(
        (acc, room) => acc + (room.infants ?? 0),
        0,
      );
      const totalPax = adults + kids + infants;
      const formatDate = (date?: Date) =>
        date
          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
          : '';
      const departDateStr = formatDate(formState.selectedDates.depart);
      const returnDateStr = formatDate(formState.selectedDates.return);
      const hotelCheckIn = formState.selectDifferentDates
        ? formatDate(formState.hotelDates.checkIn)
        : departDateStr;
      const hotelCheckOut = formState.selectDifferentDates
        ? formatDate(formState.hotelDates.checkOut)
        : formState.tripType === TripType.ONE_WAY &&
            formState.selectedDates.depart
          ? formatDate(
              addDays(
                formState.selectedDates.depart,
                DEFAULT_ONE_WAY_STAY_DAYS,
              ),
            )
          : returnDateStr;
      const checkInDate = formState.selectDifferentDates
        ? formState.hotelDates.checkIn
        : formState.selectedDates.depart;
      const checkOutDate = formState.selectDifferentDates
        ? formState.hotelDates.checkOut
        : formState.tripType === TripType.ONE_WAY &&
            formState.selectedDates.depart
          ? addDays(formState.selectedDates.depart, DEFAULT_ONE_WAY_STAY_DAYS)
          : formState.selectedDates.return;
      const lengthOfStay =
        checkInDate && checkOutDate
          ? String(differenceInDays(checkOutDate, checkInDate))
          : '1';
      const isInternational =
        formState.originStation.CountryCode !==
        formState.destinationStation.CountryCode;
      const flightType =
        formState.tripType === TripType.ROUND_TRIP ? 'RT' : 'OW';

      const searchParams = SnapSearchWidgetEvent.snapSearchButtonClickEvent({
        departdate: departDateStr,
        returndate: returnDateStr,
        checkindate: hotelCheckIn,
        checkoutdate: hotelCheckOut,
        currency: currency ?? 'MYR',
        originairport: formState.originStation.StationCode,
        destinationairport: formState.destinationStation.StationCode,
        origincountry: formState.originStation.CountryCode,
        destinationcountry: formState.destinationStation.CountryCode,
        journeytype: isInternational ? 'International' : 'Domestic',
        flighttype: flightType,
        rooms: String(formState.rooms.length),
        totalpax: totalPax,
        lengthofstay: lengthOfStay,
        adult: String(adults),
        kid: String(kids),
        infant: String(infants),
        coupon: '',
      });
      logEvent(EVENT_NAMES.SEARCH_BUTTON_CLICKED, searchParams);
    }

    setTimeout(() => {
      onSubmit?.(formState);
    }, 1000);
  };

  const searchButtonText = t('searchSnapPage.search', {
    defaultValue: 'Search',
  });

  return (
    <>
      <View style={styles.compactContainer}>
        {/* Row 1: Trip Type Selector */}
        {TripTypeSelector && snap_one_way_enabled ? (
          <View style={styles.tripTypeRow}>
            <TripTypeSelector
              tripType={formState.tripType}
              onChange={handleTripTypeChange}
            />
          </View>
        ) : null}

        {/* Row 2: Calendar, Guest fields, and Search button (web only) in a row */}
        <View style={styles.fieldsRow}>
          <View
            style={[styles.stationsContainer, styles.stationsFieldContainer]}
          >
            <SearchStationsView
              onSelect={handleStationSelect}
              isLoading={false}
              testID="snap-search-widget"
              showSearchButton={false}
              originStation={formState.originStation}
              destinationStation={formState.destinationStation}
              errors={stationErrors}
            />
          </View>
          <View style={styles.calendarAndGuestRow}>
            <View
              style={[
                styles.calendarContainer,
                styles.calendarFieldContainer,
                !snap_combined_date_field_enabled &&
                  styles.calendarContainerSplitDates,
              ]}
              testID="flight-calendar-open-trigger"
              onStartShouldSetResponderCapture={handleFlightCalendarOpenIntent}
            >
              <CalendarView
                checkInDate={formState.selectedDates.depart}
                checkOutDate={formState.selectedDates.return}
                fromDateLabel={t('searchSnapPage.departDate')}
                toDateLabel={t('searchSnapPage.returnDate')}
                isCombinedField={snap_combined_date_field_enabled}
                combinedDateLabel={t('searchSnapPage.travelDate', {
                  defaultValue: 'Travel Date',
                })}
                onConfirm={handleDateConfirm}
                onError={onError}
                onReset={handleResetDates}
                tripType={formState.tripType}
                config={calendarConfig}
                priceValues={departPriceValues}
                returnPriceValues={returnPriceValues}
                unavailablePricePlaceHolder={
                  snap_lfc_enabled ? SNAP_CALENDAR_PRICE_PLACEHOLDER : undefined
                }
                onViewableItems={handleViewableItems}
              />
            </View>
            <View
              style={[styles.guestContainer, styles.guestFieldContainer]}
              testID="guest-field-container"
            >
              <GuestFieldView
                rooms={formState.rooms}
                onConfirm={handleRoomsUpdate}
                config={SNAP_GUEST_FIELD_CONFIG}
              />
            </View>
            <View
              style={styles.searchButtonInlineContainer}
              testID="search-button-inline-container"
            >
              <ButtonView
                text={searchButtonText}
                width={101}
                onPress={handleSearch}
                type={ButtonViewType.PRIMARY_REGULAR}
                disabled={false}
              />
            </View>
          </View>
        </View>

        {/* Row 3: Checkbox for different dates */}
        <View style={styles.checkboxContainer}>
          <CheckBoxView
            isSelected={formState.selectDifferentDates}
            isDisabled={false}
            value="selectDifferentDates"
            onPress={handleSelectDifferentDatesToggle}
            label={t('searchSnapPage.selectDifferentDates')}
            testID="selectDifferentDatesCheckbox"
          />
        </View>

        {/* Row 4: Conditional hotel dates calendar */}
        {formState.selectDifferentDates && (
          <View style={styles.hotelCalendarRow}>
            <CalendarView
              checkInDate={formState.hotelDates.checkIn}
              checkOutDate={formState.hotelDates.checkOut}
              fromDateLabel={t('searchSnapPage.checkInDate')}
              toDateLabel={t('searchSnapPage.checkOutDate')}
              isCombinedField={snap_combined_date_field_enabled}
              combinedDateLabel={t('searchSnapPage.hotelDates', {
                defaultValue: 'Dates',
              })}
              onConfirm={handleHotelDateConfirm}
              onError={onError}
              onReset={handleHotelResetDates}
              tripType={TripType.ROUND_TRIP}
              config={hotelCalendarConfig}
            />
          </View>
        )}

        {/* Search button - full width with text */}
        <View
          style={styles.searchButtonFullWidthContainer}
          testID="search-button-full-width-container"
        >
          <ButtonView
            testID="search-button"
            type={ButtonViewType.PRIMARY_REGULAR}
            text={searchButtonText}
            disabled={false}
            onPress={handleSearch}
          />
        </View>
      </View>
      <ChinaDisclaimerModal
        ref={chinaDisclaimerRef}
        imageViewProps={{
          height: 186,
          width: 186,
          url: CHINA_DISCLAIMER_IMAGE_URL,
        }}
        secondaryText={`${tHotels(
          'searchhotelPage.chinaDiscalimer.redirection',
        )}\n${tHotels('searchhotelPage.chinaDiscalimer.legalDisclaimer')}`}
        redirectRoute={snapListingRedirectUrl}
        analyticsProps={{
          eventName: EVENT_NAMES.CHINA_DISCLAIMER_VIEWED,
          buildAnalyticsParams: (disclaimerMessage: string) =>
            buildSnapChinaDisclaimerViewedAnalytics({ disclaimerMessage }),
        }}
      />
    </>
  );
};

SearchFormViewContent.displayName = 'SearchFormViewContent';

const SearchFormView = (props: SearchFormViewProps) => {
  const { searchParams } = useSnapNavigation();
  const routeParams = (searchParams ?? {}) as Record<
    string,
    string | string[] | boolean | undefined
  >;
  const deeplinkParams = useMemo(
    () => parseSnapDeepLinkParams(routeParams),
    [routeParams],
  );
  const { isWeb, isMobileWidth } = useDeviceInfo();
  const { stationsMap, isStationsLoading } = useStationsMap(
    StationTypeFilter.ALL,
  );
  const { recentSearchesStore, isLoading: isRecentSearchLoading } =
    useSnapRecentSearches();
  const initialFormState = useMemo(
    () =>
      buildSnapDeepLinkInitialState({
        deeplinkParams,
        latestRecentSearch: recentSearchesStore?.recentSearches?.[0],
        stationsMap,
      }),
    [deeplinkParams, recentSearchesStore?.recentSearches, stationsMap],
  );

  const isDesktopWeb = isWeb && !isMobileWidth;
  if (
    !isDesktopWeb &&
    hasSnapDeepLinkParams(deeplinkParams) &&
    (isStationsLoading || isRecentSearchLoading)
  ) {
    return null;
  }

  return (
    <SearchFormViewContent
      key={recentSearchesStore?.recentSearches?.[0]?.id ?? 'none'}
      {...props}
      initialFormState={initialFormState}
      syncInitialFormState={hasSnapDeepLinkParams(deeplinkParams)}
    />
  );
};

SearchFormView.displayName = 'SearchFormView';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    padding: pd.ref.space.spacing40.px,
  },
  compactContainer: {
    flex: 1,
    width: '100%',
    paddingTop: pd.ref.space.spacing20.px,
    paddingBottom: pd.ref.space.spacing40.px,
    paddingHorizontal: pd.ref.space.spacing40.px,
  },
  tripTypeRow: {
    width: {
      xs: '100%',
      md: 370,
      xl: 370,
    },
    maxWidth: 370,
    marginBottom: {
      xs: pd.ref.space.spacing40.px,
      md: pd.ref.space.spacing30.px,
    },
  },
  fieldsRow: {
    flexDirection: {
      xs: 'column',
      xl: 'row',
    },
    alignItems: {
      xs: 'stretch',
      xl: 'flex-start',
    },
    gap: {
      xs: pd.ref.space.spacing40.px,
      xl: pd.ref.space.spacing30.px,
    },
    zIndex: 1,
  },
  stationsContainer: {
    flex: {
      xl: 0.5,
    },
    minWidth: 0,
    width: {
      xs: '100%',
    },
    zIndex: {
      xs: 2,
      xl: 1,
    },
  },
  stationsFieldContainer: {},
  calendarAndGuestRow: {
    flex: {
      xl: 1,
    },
    minWidth: 0,
    width: '100%',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      xs: 'stretch',
      md: 'stretch',
    },
    gap: {
      xs: pd.ref.space.spacing30.px,
      md: pd.ref.space.spacing30.px,
    },
    marginTop: {
      md: pd.ref.space.spacing30.px,
      xl: undefined,
    },
    zIndex: {
      xs: 1,
      xl: 2,
    },
  },
  calendarContainer: {
    // One combined field sizes with the row, so no minimum is needed here.
    flex: {
      xs: 1,
      md: 1.4,
      xl: 1,
    },
    minWidth: 0,
    width: {
      xs: '100%',
      md: undefined,
      xl: undefined,
    },
  },
  calendarContainerSplitDates: {
    // Applied only when the combined-date-field flag is off: two side-by-side
    // fields need enough room on tablet while still allowing wider layouts
    // to distribute the available space across the row.
    minWidth: {
      md: 328,
    },
  },
  calendarFieldContainer: {
    maxWidth: {
      md: undefined,
      xl: undefined,
    },
  },
  guestContainer: {
    flex: {
      xs: 1,
      md: 0.8,
      xl: 1,
    },
    minWidth: 0,
    width: {
      xs: '100%',
      md: undefined,
      xl: undefined,
    },
  },
  guestFieldContainer: {
    maxWidth: {
      md: undefined,
      xl: undefined,
    },
    minWidth: {
      md: 200,
    },
  },
  checkboxContainer: {
    marginTop: {
      xs: pd.ref.space.spacing40.px,
      md: 0,
    },
    marginBottom: {
      xs: pd.ref.space.spacing40.px,
      md: 0,
    },
    paddingTop: {
      xs: 0,
      md: pd.ref.space.spacing30.px,
    },
  },
  hotelCalendarRow: {
    width: {
      xs: '100%',
      md: 'auto',
    },
    minWidth: {
      md: 328,
    },
    maxWidth: {
      md: 328,
    },
    alignSelf: {
      md: 'flex-start',
    },
    marginBottom: {
      xs: pd.ref.space.spacing40.px,
      md: 0,
    },
    paddingTop: {
      xs: 0,
      md: pd.ref.space.spacing30.px,
    },
  },
  searchButtonFullWidthContainer: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    marginTop: {
      xs: 0,
    },
  },
  searchButtonInlineContainer: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    width: {
      md: 101,
      xl: 101,
    },
    marginLeft: {
      md: 0,
      xl: 0,
    },
    alignSelf: {
      md: 'center',
      xl: 'center',
    },
  },
  searchButtonContainer: {
    marginTop: pd.ref.space.spacing40.px,
  },
});

export default SearchFormView;
